import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone, ChevronDown } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/Seo";

const SITE = "https://ddsbillancourt.fr";

export default function SeoLanding({ seo, breadcrumbName, badge, badgeIcon: BadgeIcon, h1, intro = [], formules = [], sections = [], faq = [], crossLinks = [], testId }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: breadcrumbName, item: `${SITE}${seo.path}` },
        ],
      },
      {
        "@type": "Service",
        name: h1,
        serviceType: breadcrumbName,
        provider: { "@id": `${SITE}/#school` },
        areaServed: ["Boulogne-Billancourt", "Paris", "Hauts-de-Seine", "Île-de-France"],
        offers: formules
          .filter((f) => f.priceNum)
          .map((f) => ({ "@type": "Offer", name: f.name, price: String(f.priceNum), priceCurrency: "EUR" })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body" data-testid={testId}>
      <Seo {...seo} jsonLd={jsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#1a2332] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-8 pt-28 md:pt-32 pb-14 md:pb-20">
          <div className="flex w-fit items-center gap-2 text-[#FF8C42] font-headings font-bold text-sm uppercase tracking-[0.2em]">
            {BadgeIcon && <BadgeIcon className="w-4 h-4" />} {badge}
          </div>
          <h1 className="mt-4 font-headings font-black text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid={`${testId}-title`}>
            {h1}
          </h1>
          {intro.map((p, i) => (
            <p key={i} className="mt-4 text-white/80 max-w-3xl text-base md:text-lg leading-relaxed">{p}</p>
          ))}
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm shadow-md transition-colors" data-testid={`${testId}-cta-inscription`}>
              S&apos;inscrire <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+33141315061" className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-[#00a86b] hover:text-[#00a86b] font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors" data-testid={`${testId}-cta-tel`}>
              <Phone className="w-4 h-4" /> 01 41 31 50 61
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-8 py-12 md:py-16">
        {/* Formules */}
        {formules.length > 0 && (
          <div className="grid md:grid-cols-2 gap-5" data-testid={`${testId}-formules`}>
            {formules.map((f, i) => (
              <div key={i} className={`relative bg-white rounded-2xl border p-7 shadow-sm ${f.highlight ? "border-[#00a86b] ring-2 ring-[#00a86b]/20" : "border-black/5"}`}>
                {f.highlight && (
                  <span className="absolute -top-3 left-6 bg-[#00a86b] text-white text-[11px] font-headings font-bold rounded-full px-3 py-1 uppercase tracking-wide">Le plus choisi</span>
                )}
                <h3 className="font-headings font-black text-xl">{f.name}</h3>
                <div className="mt-3 font-headings font-black text-4xl text-[#00a86b]">
                  {f.price} <span className="text-sm text-[#5b6577] font-semibold">TTC</span>
                </div>
                <ul className="mt-4 space-y-2">
                  {f.features.map((ft, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#1a2332]/85">
                      <Check className="w-4 h-4 text-[#00a86b] mt-0.5 shrink-0" /> {ft}
                    </li>
                  ))}
                </ul>
                <Link to="/#contact" className="mt-6 inline-flex items-center gap-2 bg-[#1a2332] hover:bg-[#00a86b] text-white font-headings font-bold rounded-xl px-5 py-2.5 text-sm transition-colors" data-testid={`${testId}-formule-${i}-cta`}>
                  S&apos;inscrire <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Content sections */}
        {sections.map((s, i) => (
          <section key={i} className="mt-14">
            <h2 className="font-headings font-black text-2xl md:text-3xl tracking-tight">{s.title}</h2>
            {(s.paragraphs || []).map((p, j) => (
              <p key={j} className="mt-4 text-[#3a4557] leading-relaxed">{p}</p>
            ))}
            {s.bullets && (
              <ul className="mt-5 space-y-3">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-3 bg-white rounded-xl border border-black/5 px-5 py-3.5 text-sm md:text-base text-[#1a2332]/90 shadow-sm">
                    <Check className="w-5 h-5 text-[#00a86b] mt-0.5 shrink-0" />
                    <span><strong className="font-headings">{b.title}</strong>{b.title ? " — " : ""}{b.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="mt-14" data-testid={`${testId}-faq`}>
            <h2 className="font-headings font-black text-2xl md:text-3xl tracking-tight">Questions fréquentes</h2>
            <div className="mt-6 space-y-3">
              {faq.map((f, i) => (
                <details key={i} className="group bg-white rounded-xl border border-black/5 shadow-sm px-5 py-4">
                  <summary className="flex items-center justify-between cursor-pointer list-none font-headings font-bold text-[#1a2332]">
                    {f.q}
                    <ChevronDown className="w-4 h-4 text-[#5b6577] group-open:rotate-180 transition-transform shrink-0 ml-3" />
                  </summary>
                  <p className="mt-3 text-sm text-[#3a4557] leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Cross links */}
        {crossLinks.length > 0 && (
          <section className="mt-14">
            <h2 className="font-headings font-black text-xl">Nos autres formations à Boulogne-Billancourt</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {crossLinks.map((c, i) => (
                <Link key={i} to={c.to} className="inline-flex items-center gap-2 bg-white border border-black/10 hover:border-[#00a86b] hover:text-[#00a86b] font-headings font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors">
                  {c.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA banner */}
        <div className="mt-16 rounded-2xl bg-[#1a2332] text-white p-8 md:p-10 text-center">
          <div className="font-headings font-black text-2xl">Une question ? Parlons de votre projet.</div>
          <p className="mt-2 text-white/70">Bureau au 37 Av. Édouard Vaillant, 92100 Boulogne-Billancourt · Conduite 7j/7 de 7h à 22h.</p>
          <div className="mt-5 flex flex-wrap gap-3 justify-center">
            <a href="tel:+33141315061" className="inline-flex items-center gap-2 bg-[#00a86b] hover:bg-[#00915d] text-white font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors">
              <Phone className="w-4 h-4" /> 01 41 31 50 61
            </a>
            <Link to="/reservation" className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-[#FF8C42] hover:text-[#FF8C42] font-headings font-bold rounded-xl px-6 py-3 text-sm transition-colors">
              Réserver ma première leçon <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
